import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowRight, ShieldCheck, Clock, Plane, Wine, Briefcase, MessageSquare, Phone, Check } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import CallbackForm from "@/components/CallbackForm";
import QuoteForm from "@/components/QuoteForm";
import { AIRPORT_RATES } from "@/data/rates";
import api from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { useSEO, BUSINESS_LD } from "@/lib/seo";

const HERO = "https://images.unsplash.com/photo-1771775751121-3091d79073d4";
const ASPEN_BG = "https://customer-assets-agu9un31.emergentagent.net/job_luxury-transport-hub-5/artifacts/ubfcqy49_shutterstock_1841153455-2%20-%20Copy.webp";
const WHY_BG = "/gondola_clean_v3.png";

const services = [
  { icon: Plane, title: "Airport Transportation", desc: "Luxury SUV transfers to and from Aspen (ASE), Eagle (EGE), Denver (DEN), Rifle (RIL) & Grand Junction (GJT) — plus private jet FBO service.", link: "/airport-transportation" },
  { icon: Briefcase, title: "Corporate Travel", desc: "Punctual, discreet chauffeurs for executives and visiting clients." },
  { icon: Wine, title: "Aspen Area Transportation", desc: "Luxury transportation for dining, shopping, concerts, special events, hotels, and destinations throughout Aspen and the Roaring Fork Valley.", link: "/aspen-area-transportation" },
  { icon: Clock, title: "Hourly Hire", desc: "A car and chauffeur at your disposal for the hour, the day, or the week. Please call us directly so we can discuss your individual needs and tailor a quote that reflects your requirements." },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [airportRates, setAirportRates] = useState(AIRPORT_RATES);

  useSEO({
    title: "Aspen Executive Limo | Luxury Private Car Service in Aspen, CO",
    description:
      "Aspen's premier luxury private car service. Chauffeured executive SUV transportation, airport transfers, corporate travel, and events across Aspen, Vail, Denver & the Roaring Fork Valley.",
    keywords:
      "Aspen limo service, Aspen car service, luxury transportation Aspen, private chauffeur Aspen, Aspen airport transportation, executive SUV Aspen, Roaring Fork Valley car service",
    canonical: "https://www.aspenexecutivelimos.com/",
    image: HERO,
    jsonLd: BUSINESS_LD,
  });

  const scrollToQuote = () => {
    document.getElementById("request-quote")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  useEffect(() => {
    api.get("/vehicles?category=suv").then((res) => setFeatured(res.data.slice(0, 1))).catch(() => {});
    api.get("/rates").then((res) => { if (res.data?.airport?.length) setAirportRates(res.data.airport); }).catch(() => {});
  }, []);

  return (
    <div className="relative min-h-screen text-white isolate" data-testid="home-page">
      {/* Fixed full-screen Aspen background */}
      <div className="fixed inset-0 z-0" aria-hidden="true" data-testid="home-background">
        <img
          src={ASPEN_BG}
          alt=""
          className="w-full h-full object-cover object-center"
          style={{ filter: "brightness(1.25) saturate(1.3) contrast(1.05)" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0B]/20 via-transparent to-[#0A0A0B]/35" />
      </div>

      <div className="relative z-10">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-black/10 to-transparent" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 flex flex-col pt-[84px] pb-16">
          <div className="max-w-2xl animate-fade-up">
            <h1 className="font-heading text-5xl sm:text-7xl font-light leading-[1.05] tracking-tight text-left">
              Aspen Executive Limo
              <span className="block text-white text-3xl sm:text-5xl mt-2">Elite Service at Unmatched Value</span>
            </h1>
            <div className="mt-6 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => { trackEvent("reservation_click", { source: "hero_reserve" }); scrollToQuote(); }}
                data-testid="hero-reserve-ride-btn"
                className="inline-flex items-center justify-center bg-[#D4AF37] text-black font-bold text-base tracking-wide rounded-sm px-[3px] py-[1px] shadow-xl hover:bg-[#E0BC4A] transition-colors"
              >
                Reserve Your Luxury Ride
              </button>
              <Link
                to="/rates"
                data-testid="mobile-view-rates-btn"
                className="inline-flex items-center justify-center bg-[#D4AF37] text-black font-bold text-base tracking-wide rounded-sm px-[3px] py-[1px] shadow-xl hover:bg-[#E0BC4A] transition-colors"
              >
                View Our Rates
              </Link>
            </div>
            <p
              className="italic font-semibold text-base sm:text-lg mt-4 max-w-xl leading-relaxed text-zinc-100"
              data-testid="hero-mission-statement"
              style={{ textShadow: "0 2px 10px rgba(0,0,0,0.95), 0 1px 3px rgba(0,0,0,0.98)" }}
            >
              Uncompromising luxury service. Clearly the lowest prices among comparable transportation providers in the Valley.
            </p>
            <div className="mt-[26px]">
              <p className="italic font-semibold text-base sm:text-lg text-zinc-100" style={{ textShadow: "0 2px 10px rgba(0,0,0,0.95), 0 1px 3px rgba(0,0,0,0.98)" }}>
                Contact us directly by text (preferred method) or call
              </p>
              <div className="flex flex-wrap items-center gap-3 mt-3">
                <a
                  href="sms:+19703156500"
                  onClick={() => trackEvent("sms_click", { source: "hero" })}
                  data-testid="hero-contact-text-btn"
                  className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-black font-bold hover:bg-[#E0BC4A] transition-colors rounded-sm px-[3px] py-[1px] text-base tracking-wide shadow-xl"
                >
                  <MessageSquare size={18} strokeWidth={2.2} /> Text 970-315-6500
                </a>
                <a
                  href="tel:+19703156500"
                  onClick={() => trackEvent("phone_click", { source: "hero" })}
                  data-testid="hero-contact-call-btn"
                  className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-black font-bold hover:bg-[#E0BC4A] transition-colors rounded-sm px-[3px] py-[1px] text-base tracking-wide shadow-xl"
                >
                  <Phone size={18} strokeWidth={2.2} /> Call 970-315-6500
                </a>
              </div>
            </div>
            <p
              className="italic font-semibold text-base sm:text-lg mt-[32px] max-w-xl leading-relaxed text-zinc-100"
              data-testid="hero-airport-transport"
              style={{ textShadow: "0 2px 10px rgba(0,0,0,0.95), 0 1px 3px rgba(0,0,0,0.98)" }}
            >
              Luxury airport transportation to Aspen, Denver, Eagle, Grand Junction, Rifle, Glenwood Springs, Carbondale, Basalt, and Snowmass. Private Jet FBO transfers.
            </p>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="max-w-7xl mx-auto px-6 pt-2 pb-24">
        <div className="mb-8 max-w-3xl text-left">
          <p
            className="font-semibold text-3xl sm:text-4xl tracking-tight mb-3 text-white"
            style={{ textShadow: "0 1px 3px rgba(0,0,0,0.6)" }}
          >
            Elite Service at Unmatched Value.
          </p>
          <p
            className="mt-4 leading-relaxed text-lg sm:text-xl font-semibold text-zinc-100"
            style={{ textShadow: "0 1px 3px rgba(0,0,0,0.6)" }}
          >
            Our knowledgeable, professional local chauffeurs and luxury four wheel drive SUVs provide comfortable transportation with punctual, personalized service built around your schedule.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch">
          {services.map((s) => (
            <div
              key={s.title}
              data-testid={`service-${s.title}`}
              className="group bg-black/40 backdrop-blur-md border border-white/10 rounded-sm p-10 min-h-[300px] flex flex-col hover:border-[#D4AF37]/40 hover:-translate-y-1 transition-all duration-500"
            >
              <s.icon size={28} strokeWidth={1.5} className="gold-text mb-6" />
              <h3 className="font-heading text-2xl font-normal mb-3">{s.title}</h3>
              <p className="text-zinc-400 font-light text-sm leading-relaxed">{s.desc}</p>
              {s.link && (
                <Link
                  to={s.link}
                  data-testid={`service-learn-more-${s.title}`}
                  className="mt-auto pt-6 inline-flex items-center gap-2 gold-text font-heading text-2xl font-normal hover:gap-3 transition-all"
                >
                  Learn More <ArrowRight size={26} />
                </Link>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* FEATURED FLEET */}
      <section className="relative bg-black/40 backdrop-blur-md border-y border-white/10 pt-[58px] pb-28">
        <div className="max-w-7xl mx-auto px-6">
          <div className="pt-[16px] mb-[17px]">
            <div className="flex flex-wrap items-center gap-6">
              <h2 className="font-heading text-4xl sm:text-5xl font-light tracking-tight">Featured Vehicles</h2>
              <Link to="/fleet#fleet-photo" data-testid="view-all-fleet-btn" className="flex items-center gap-2 bg-[#D4AF37] text-black hover:bg-[#E0BC4A] transition-all duration-300 rounded-sm px-5 py-2 text-sm font-medium tracking-wide">
                View all <ArrowRight size={16} />
              </Link>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 items-stretch">
            {featured.map((v) => (
              <div
                key={v.id}
                data-testid={`featured-vehicle-${v.id}`}
                className="group flex flex-col bg-black/50 backdrop-blur-md border border-white/10 rounded-sm overflow-hidden hover:border-[#D4AF37]/40 transition-all duration-500"
              >
                <Link to={`/fleet/${v.id}`} className="suv-stage block h-72 overflow-hidden">
                  <img src={v.image_url} alt={v.name} className="suv-image w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </Link>
                <div className="p-6 flex-1 flex flex-col">
                  <p className="font-heading text-2xl">With our four-wheel drive SUVs and expert chauffeurs, you can count on safe, all-weather transportation, so you arrive on time.</p>
                  <div className="flex flex-wrap gap-3 mt-auto pt-6">
                    <Link
                      to={`/fleet/${v.id}#request-quote`}
                      data-testid={`featured-quote-btn-${v.id}`}
                      className="group/q bg-[#D4AF37] text-black hover:bg-[#E0BC4A] transition-all duration-300 rounded-sm px-[3px] py-[1px] text-sm font-bold inline-flex items-center justify-center gap-2"
                    >
                      Request a Quote <ArrowRight size={14} className="group-hover/q:translate-x-0.5 transition-transform" />
                    </Link>
                    <Link
                      to={`/fleet/${v.id}`}
                      data-testid={`featured-reserve-btn-${v.id}`}
                      className="group/r bg-[#D4AF37] text-black hover:bg-[#E0BC4A] transition-all duration-300 rounded-sm px-[3px] py-[1px] text-sm font-bold inline-flex items-center justify-center gap-2"
                    >
                      Reserve <ArrowRight size={14} className="group-hover/r:translate-x-0.5 transition-transform" />
                    </Link>
                  </div>
                </div>
              </div>
            ))}

            {/* Airport Rates */}
            <div data-testid="airport-rates-panel" className="flex flex-col bg-black/50 backdrop-blur-md border border-white/10 rounded-sm overflow-hidden">
              <div className="p-6 sm:p-8 flex-1 flex flex-col">
                <h3 className="font-heading text-3xl font-light tracking-tight mb-6">Airport Rates</h3>
                <div className="overflow-hidden rounded-sm border border-white/10">
                  <div className="grid grid-cols-[1.4fr_1.4fr_0.8fr] gap-2 px-4 py-3 bg-white/[0.04] border-b border-white/10 text-[10px] sm:text-[11px] tracking-label uppercase gold-text">
                    <span>From</span>
                    <span>To</span>
                    <span className="text-right">Flat Rate</span>
                  </div>
                  {airportRates.map((r) => (
                    <div key={r.to} className="grid grid-cols-[1.4fr_1.4fr_0.8fr] gap-2 items-center px-4 py-4 border-b border-white/[0.06] last:border-0">
                      <span className="text-white font-medium text-sm">{r.from}</span>
                      <span className="text-zinc-400 font-light text-sm">{r.to}</span>
                      <span className="text-right font-heading text-xl gold-text">${r.rate}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-auto pt-6">
                  <Link to="/rates" data-testid="airport-rates-view-all" className="group/v inline-flex items-center gap-2 bg-[#D4AF37] text-black hover:bg-[#E0BC4A] transition-all duration-300 rounded-sm px-[3px] py-[1px] text-sm font-bold">
                    View all area rates <ArrowRight size={14} className="group-hover/v:translate-x-0.5 transition-transform" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST */}
      <section className="relative isolate overflow-hidden bg-[#0b0f14] py-28 text-white font-bold" style={{ textShadow: "0 2px 8px rgba(0,0,0,0.9), 0 1px 2px rgba(0,0,0,0.95)" }}>
        <div className="absolute inset-x-0 top-0 z-0" aria-hidden="true" data-testid="why-choose-bg">
          <div className="relative w-full">
            <img src={WHY_BG} alt="" className="w-full h-auto block" />
            <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-b from-transparent to-[#0b0f14]" />
          </div>
        </div>
        <div className="relative z-10 max-w-3xl mx-auto px-6">
        <h2 className="font-heading text-[38px] font-bold tracking-tight leading-tight" data-testid="why-choose-heading" style={{ textShadow: "0 2px 10px rgba(0,0,0,0.98), 0 1px 3px rgba(0,0,0,1), 0 0 2px rgba(0,0,0,0.9)" }}>
          The Aspen Executive Limo Difference
        </h2>
        <p className="font-heading text-2xl sm:text-3xl font-bold text-white mt-3" style={{ textShadow: "0 2px 9px rgba(0,0,0,0.99), 0 1px 2px rgba(0,0,0,1)" }}>
          Elite Service at Unmatched Value.
        </p>
        <ul className="mt-6 space-y-3">
          {[
            "Professional, courteous chauffeurs",
            "Luxury SUVs for comfort and safety",
            "Airport transfers with flight tracking",
            "Serving Aspen, Snowmass, Basalt, Carbondale, Vail, Eagle, and Denver",
            "Transparent pricing with no hidden fees",
            "Available 24 hours a day, 7 days a week",
            "Family owned and locally operated, with a personal commitment to exceptional service",
          ].map((item) => (
            <li key={item} className="flex items-start gap-3 font-bold text-lg leading-relaxed">
              <Check size={20} strokeWidth={2} className="gold-text shrink-0 mt-1" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
        <div className="mt-12 grid sm:grid-cols-2 gap-8">
          {[
            { icon: ShieldCheck, t: "Fully Licensed & Insured", d: "Complete peace of mind on every trip." },
            { icon: Clock, t: "Always On Time", d: "Live flight tracking and route monitoring." },
          ].map((f) => (
            <div key={f.t}>
              <f.icon size={24} strokeWidth={1.5} className="gold-text mb-4" />
              <h4 className="font-bold text-lg">{f.t}</h4>
              <p className="font-bold text-lg mt-1">{f.d}</p>
            </div>
          ))}
        </div>
        </div>
      </section>

      {/* REQUEST A QUOTE */}
      <section id="request-quote" className="scroll-mt-24 relative bg-black/40 backdrop-blur-md border-t border-white/10 py-28">
        <div className="max-w-2xl mx-auto px-6">
          <div className="text-center mb-10">
            <p className="relative top-[10px] text-2xl font-bold tracking-label uppercase text-white mb-4">Elite Service at Unmatched Value</p>
            <h2 className="relative top-[10px] font-heading text-4xl sm:text-5xl font-light tracking-tight">
              Reserve Your Luxury SUV
            </h2>
          </div>
          <QuoteForm />
        </div>
      </section>

      <CallbackForm />

      <Footer />
      </div>
    </div>
  );
}
